import { useEffect, useState, createContext, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useDatoClient } from '@src/stores-v2/StoreContext';
import { TranscriptEditorStore } from '../TranscriptEditorStore';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { TextElement, PunctElement } from '../models/TranscriptElement';
import { analytics } from '@src/utility/analytics';
import { TimedWord, TranscriptEditorChangeType, TranscriptEditorObserver } from '../types';
import SimpleLoadingSpinner from '../../components/SimpleLoadingSpinner';
import { TranscriptWaveform } from './TranscriptWaveform';
import TranscriptView from './TranscriptView';
import FineAlignmentModal from './modals/FineAlignmentModal';
import TranscriptVersionSwitchModal from './modals/TranscriptVersionSwitchModal';
import SpeakersPanel from './SpeakersPanel';
import VideoPlayerPreview from './VideoPlayerPreview';
import { TranscriptVersion } from '../../repositories/TranscriptRepository';
import CloseIcon from '@src/svgs/CloseIcon';
import { VideoPlayerProvider } from '../hooks/VideoPlayerContext';
import TranscriptEditorHeader from './TranscriptEditorHeader';

// Declare global debug types for alignment
declare global {
  interface Window {
    _arborAlignmentDebug?: any;
  }
}

interface TranscriptEditorWrapperProps {
  editorStore: TranscriptEditorStore;
  standalone?: boolean;
  onClose?: () => void;
}

interface AlignmentResult {
  before: Array<{
    word: string;
    startTime?: number;
    endTime?: number;
  }>;
  after: Array<{
    word: string;
    startTime: number;
    endTime: number;
    confidence?: number;
  }>;
}

interface AlignmentResult {
  before: Array<{
    word: string;
    startTime?: number;
    endTime?: number;
  }>;
  after: Array<{
    word: string;
    startTime: number;
    endTime: number;
    confidence?: number;
  }>;
}


/**
 * TranscriptEditorWrapper - Component for editing transcripts
 */
const TranscriptEditorWrapper = observer((props: TranscriptEditorWrapperProps) => {
  const { editorStore, standalone = true, onClose } = props;
  const datoClient = useDatoClient();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [showAlignmentModal, setShowAlignmentModal] = useState(false);
  const [alignmentResult, setAlignmentResult] = useState<AlignmentResult | null>(null);
  const [processingSelection, setProcessingSelection] = useState(false);
  
  // Fine alignment modal state
  const [showFineAlignmentModal, setShowFineAlignmentModal] = useState(false);
  const [fineAlignmentPosition, setFineAlignmentPosition] = useState({ x: 0, y: 0 });
  const [selectedElementsForAlignment, setSelectedElementsForAlignment] = useState<Array<{
    id: string;
    segmentId: string;
    paragraphId: string;
    type: string;
  }>>([]);

  // Search term for transcript filtering
  const [searchTerm, setSearchTerm] = useState('');

  // Set of paragraph ids that match current search (null = no active filter)
  const [visibleParagraphIds, setVisibleParagraphIds] = useState<Set<string> | null>(null);

  // Web Worker for background search (initialised once)
  const searchWorkerRef = useRef<Worker | null>(null);

  // Build worker on mount
  useEffect(() => {
    // Dynamically import worker (works with CRA + webpack >=5)
    const worker = new Worker(new URL('../workers/TranscriptSearchWorker.ts', import.meta.url), { type: 'module' });
    searchWorkerRef.current = worker;

    worker.onmessage = (e: MessageEvent) => {
      const { type, term, ids } = e.data as any;
      if (type === 'results') {
        if (!term) {
          setVisibleParagraphIds(null);
        } else {
          setVisibleParagraphIds(new Set<string>(ids));
        }
      }
    };

    return () => {
      worker.terminate();
    };
  }, []);

  // Send transcript data to worker when container changes
  useEffect(() => {
    if (!editorStore.container) return;
    if (!searchWorkerRef.current) return;

    const paragraphs = editorStore.container.segments.flatMap(segment =>
      segment.paragraphs.map(p => ({ id: p.id, text: p.getText() }))
    );

    searchWorkerRef.current.postMessage({ type: 'build', paragraphs });
  }, [editorStore.container]);

  // Debounced relay search term to worker
  useEffect(() => {
    const term = searchTerm.trim();

    if (!searchWorkerRef.current) return;

    const handle = setTimeout(() => {
      searchWorkerRef.current?.postMessage({ type: 'search', term });
    }, 150);

    return () => clearTimeout(handle);
  }, [searchTerm]);
  
  // Version switching state
  const [showVersionSwitchModal, setShowVersionSwitchModal] = useState(false);
  const [targetVersion, setTargetVersion] = useState<TranscriptVersion | null>(null);

  // Use analytics to track component mount and initialize
  useEffect(() => {
    try {
      
      // Track editor opening with story ID
      const storyId = editorStore.editor.storyId;
      analytics.track('transcript_editor_opened', {
        storyId,
        language: editorStore.language,
      });
      
      // Cleanup on unmount
      return () => {
        analytics.track('transcript_editor_closed', {
          storyId: editorStore.editor.storyId,
          language: editorStore.language,
        });
      };
    } catch (error) {
      console.error('Error in transcript editor initialization:', error);
    }
  }, [editorStore, datoClient]);
  
  // Ensure container is populated when mounting (e.g. returning to this tab)
  useEffect(() => {
    if (editorStore.editor) {
      const transcription = editorStore.editor.getTranscription();
      editorStore.setTranscription(transcription);
    }
  }, [editorStore]);

  // Handle undo action
  const handleUndo = async () => {
    if (!editorStore.editor) return;
    
    if (editorStore.getUndoStackSize() > 0) {
      try {
        // Call the undo method on the store which handles the command manager interaction
        const result = await editorStore.undoLastOperation();
        
        if (result) {
          // Track undo in analytics
          analytics.track('transcript_command_undone', {
            storyId: editorStore.editor.storyId,
            language: editorStore.language,
          });
          
          // The observer will update the stack sizes automatically
        }
      } catch (error) {
        console.error('Error during undo:', error);
      }
    }
  };
  
  // Handle redo action
  const handleRedo = async () => {
    if (!editorStore.editor) return;
    
    if (editorStore.getRedoStackSize() > 0) {
      try {
        // Call the redo method on the store which handles the command manager interaction
        const result = await editorStore.redoLastOperation();
        
        if (result) {
          // Track redo in analytics
          analytics.track('transcript_command_redone', {
            storyId: editorStore.editor.storyId,
            language: editorStore.language,
          });
          
          // The observer will update the stack sizes automatically
        }
      } catch (error) {
        console.error('Error during redo:', error);
      }
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      
      // Ctrl/Cmd + C to copy selected text
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        // Check if we have a text selection in the transcript
        const selectionService = editorStore.editor?.selectionService;
        if (selectionService?.hasSelection && selectionService?.currentSelectionType === 'elements') {
          e.preventDefault();
          
          const selectedElements = selectionService.getSelectedElements();
          
          // Extract text from selected elements
          let textToCopy = '';
          let prevSegmentId = '';
          let prevParagraphId = '';
          for (const selectedElement of selectedElements) {
            if (editorStore.container) {
              const seg = editorStore.container.getSegment(selectedElement.segmentId);
              if (seg) {
                if (prevSegmentId !== selectedElement.segmentId) {
                  prevSegmentId = selectedElement.segmentId;
                  prevParagraphId = '';
                  textToCopy += '\n\n';
                }
                const para = seg.getParagraph(selectedElement.paragraphId);
                if (para) {
                  if (prevParagraphId !== selectedElement.paragraphId) {
                    prevParagraphId = selectedElement.paragraphId;
                    textToCopy += '\n\n';
                  }
                  const elem = para.getElement(selectedElement.id);
                  if (elem && elem.type === 'text' && 'value' in elem) {
                    textToCopy += (textToCopy ? ' ' : '') + elem.value;
                  }
                }
              }
            }
          }
          
          if (textToCopy) {
            const { handleCopyToClipboard } = await import('../../utility/general');
            await handleCopyToClipboard(textToCopy);
            console.log('[TranscriptEditor] Copied text to clipboard:', textToCopy);
            
            // Track analytics
            analytics.track('transcript_text_copied', {
              storyId: editorStore.editor.storyId,
              language: editorStore.language,
              textLength: textToCopy.length,
              elementCount: selectedElements.length
            });
          }
        }
      }
      
      // Ctrl/Cmd + Z to undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      
      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y to redo
      if (((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) || 
          ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
      
      // Escape to cancel discard confirmation
      if (e.key === 'Escape' && confirmingDiscard) {
        setConfirmingDiscard(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editorStore, confirmingDiscard]);
  
  // Handle fine alignment for the current selection using SelectionService
  const handleFineAlignment = async () => {
    if (!editorStore.container) {
      alert('No transcript data loaded');
      return;
    }
    
    // Get current position for the modal
    const position = { 
      x: window.innerWidth / 2, 
      y: window.innerHeight / 3 
    };
    
    // Get the selected elements from the selection service in the editor
    const selectionService = editorStore.editor.getSelectionService();
    if (!selectionService) {
      alert('Selection service not available');
      return;
    }
    
    const selectedElements = selectionService.getSelectedElements();
    
    if (selectedElements.length === 0) {
      alert('No text selected. Please select text to fine-tune alignment.');
      return;
    }
    
    // Convert selected elements to the format needed by the FineAlignmentModal
    const elementData = selectedElements.map((el: any) => ({
      id: el.id,
      segmentId: el.segmentId,
      paragraphId: el.paragraphId,
      type: el.type
    }));
    
    setSelectedElementsForAlignment(elementData);
    setFineAlignmentPosition(position);
    setShowFineAlignmentModal(true);
    
    // Track analytics
    analytics.track('transcript_fine_alignment_opened', {
      storyId: editorStore.editor.storyId,
      language: editorStore.language,
      elementCount: elementData.length
    });
  };
  
  // Process the current selection with forced alignment using SelectionService
  const handleProcessCurrentSelection = async () => {
    if (!editorStore.container) {
      alert('No transcript data loaded');
      return;
    }
    
    // Get the selection service from the editor
    const selectionService = editorStore.editor?.getSelectionService();
    if (!selectionService) {
      alert('Selection service not available');
      return;
    }
    
    // Get the selected elements from SelectionService
    const selectedServiceElements = selectionService.getSelectedElements();
    
    if (selectedServiceElements.length === 0) {
      alert('No text selected. Please select text to process with forced alignment.');
      return;
    }
    
    // Convert selected elements to the format needed by processSelection
    const selectedElements = selectedServiceElements.map((el: any) => ({
      elementId: el.id,
      segmentId: el.segmentId,
      paragraphId: el.paragraphId,
      element: editorStore.container?.getSegment(el.segmentId)
        ?.getParagraph(el.paragraphId)
        ?.getElement(el.id) as TextElement | PunctElement
    })).filter((item: any) => item.element !== null);
    
    // Process the selection
    processSelection(selectedElements);
    
    // Track analytics
    analytics.track('transcript_forced_alignment_initiated', {
      storyId: editorStore.editor.storyId,
      language: editorStore.language,
      elementCount: selectedElements.length
    });
  };

  // Extracted method to process the selected elements
  const processSelection = (selectedElements: Array<{
    elementId: string;
    segmentId: string;
    paragraphId: string;
    element: TextElement | PunctElement;
  }>) => {
    
    // Filter text elements only
    const textElements = selectedElements.filter(el => 
      el.element && el.element.type === 'text'
    ) as Array<{
      elementId: string;
      segmentId: string;
      paragraphId: string;
      element: TextElement;
    }>;
    
    if (textElements.length === 0) {
      alert('No text elements in selection. Please select text elements to process.');
      return;
    }
    
    try {
      // Initialize debug data for this alignment session
      if (typeof window !== 'undefined') {
        window._arborAlignmentDebug = window._arborAlignmentDebug || {};
        window._arborAlignmentDebug.selectionAlignment = {
          originalSelection: textElements.map(el => ({
            id: el.elementId,
            segmentId: el.segmentId,
            paragraphId: el.paragraphId,
            type: el.element.type,
            value: el.element.value,
            startTime: el.element.startTime,
            endTime: el.element.endTime
          }))
        };
      }
      
      // Find the full text of the selected elements
      const fullText = textElements.map(el => el.element.value).join(' ');
      
      // Calculate start and end times for the selection
      const startTimes = textElements
        .map(el => el.element.startTime)
        .filter(t => t !== undefined) as number[];
      
      const endTimes = textElements
        .map(el => el.element.endTime)
        .filter(t => t !== undefined) as number[];
      
      const startTime = startTimes.length > 0 ? Math.min(...startTimes) : undefined;
      const endTime = endTimes.length > 0 ? Math.max(...endTimes) : undefined;
      
      if (startTime === undefined || endTime === undefined) {
        alert('Selected text does not have timing information. Cannot process.');
        return;
      }
      
      // Extract words with their timing information
      const beforeWords: Array<{word: string; startTime?: number; endTime?: number}> = [];
      
      // For each text element, extract its individual words
      textElements.forEach(el => {
        if (el.element.value) {
          const words = el.element.value.trim().split(/\s+/);
          
          // If this element has a single word, we can use its timing directly
          if (words.length === 1) {
            beforeWords.push({
              word: words[0],
              startTime: el.element.startTime,
              endTime: el.element.endTime
            });
          } 
          // For multi-word elements, we'll need to do time interpolation
          else if (words.length > 1 && el.element.startTime !== undefined && el.element.endTime !== undefined) {
            const duration = el.element.endTime - el.element.startTime;
            const wordDuration = duration / words.length;
            
            words.forEach((word, i) => {
              beforeWords.push({
                word,
                startTime: el.element.startTime! + (i * wordDuration),
                endTime: el.element.startTime! + ((i + 1) * wordDuration)
              });
            });
          }
          // For elements without timing, just add the words without timing
          else {
            words.forEach(word => {
              beforeWords.push({ word });
            });
          }
        }
      });
      
      // Process the selection with alignment
      const firstElement = textElements[0];
      const lastElement = textElements[textElements.length - 1];
      
      // Set processing state
      setProcessingSelection(true);
      
      // Add the selection to the editor's untimed text tracking
      // We'll use the first element's info for the segment, paragraph, and position
      editorStore.editor.addUntimedText(
        firstElement.segmentId,
        firstElement.paragraphId,
        // Use first element's ID as a reference point, but the aligned text will span the selection
        firstElement.elementId,
        fullText,
        'middle' // Treat as middle position
      );
      
      // Setup listener for alignment completion
      const handleAlignmentDone: TranscriptEditorObserver = {
        onEditorChange: (
          editor: TranscriptEditorCore, 
          changeType: TranscriptEditorChangeType, 
          payload?: any
        ) => {
          if (changeType === TranscriptEditorChangeType.UNTIMED_TEXT_PROCESSED) {
            // Stop listening
            editorStore.editor.removeObserver(handleAlignmentDone);
            
            // Get the alignment result from the debug object
            if (typeof window !== 'undefined' && window._arborAlignmentDebug?.selectionAlignment) {
              const alignmentDebug = window._arborAlignmentDebug;
              
              // Check if we have alignment results
              if (payload && payload.results && Array.isArray(payload.results.words)) {
                const result = payload.results;
                
                // Process the result to create a before/after comparison
                const afterWords = result.words.map((word: TimedWord) => ({
                  word: word.word,
                  startTime: word.start,
                  endTime: word.end,
                  confidence: word.confidence
                }));
                
                // Store result in debug data
                if (window._arborAlignmentDebug && window._arborAlignmentDebug.selectionAlignment) {
                  window._arborAlignmentDebug.selectionAlignment.alignmentResult = result;
                  window._arborAlignmentDebug.selectionAlignment.beforeAfterComparison = {
                    before: beforeWords,
                    after: afterWords
                  };
                }
                
                // Set results for display in modal
                setAlignmentResult({
                  before: beforeWords,
                  after: afterWords
                });
                
                // Show the modal with results
                setShowAlignmentModal(true);
              }
            }
            
            // Clear processing state
            setProcessingSelection(false);
          }
        }
      };
      
      // Add observer to listen for alignment completion
      editorStore.editor.registerObserver(handleAlignmentDone);
      
      // Trigger the alignment processing
      editorStore.editor.triggerUntimedTextProcessing();
      
      // Track in analytics
      analytics.track('transcript_selection_alignment', {
        storyId: editorStore.editor.storyId,
        language: editorStore.language,
        wordCount: beforeWords.length,
        elementCount: textElements.length,
        selectionStartTime: startTime,
        selectionEndTime: endTime
      });
    } catch (error) {
      console.error('Error processing selection:', error);
      setProcessingSelection(false);
      alert(`Error processing selection: ${error instanceof Error ? error.message : String(error)}`);
    }
  };
  
  // Render error state
  if (editorStore.error) {
    return (
      <ErrorContainer>
        <ErrorTitle>Error</ErrorTitle>
        <ErrorMessage>{editorStore.error}</ErrorMessage>
        <Button onClick={() => editorStore.error = null}>Dismiss</Button>
      </ErrorContainer>
    );
  }
  
  // Render loading state when explicitly loading
  if (editorStore.isLoading) {
    return (
      <LoadingContainer>
        <SimpleLoadingSpinner text="Loading transcript..." />
      </LoadingContainer>
    );
  }
  
  
  return (
    <VideoPlayerProvider>
      <Container standalone={standalone}>
        <TranscriptEditorHeader
          editorStore={editorStore}
          onClose={onClose}
          onVersionConfirmationRequired={(version) => {
            setTargetVersion(version);
            setShowVersionSwitchModal(true);
          }}
          standalone={standalone}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
        />
      
      {/* Transcript editor main content area */}
      <ChunksContainer>
        {!editorStore.container || editorStore.container.segments.length === 0 ? (
          <LoadingContainer>
            <SimpleLoadingSpinner text="Loading transcript data..." />
            <DebugSection>
              <div>Language: {editorStore.language}</div>
              <div>Story ID: {editorStore.editor.storyId}</div>
              <div>Album ID: {editorStore.getAlbumId()}</div>
              <div>Audio URL: {editorStore.getAudioUrl() ? 'Available' : 'Not available'}</div>
              <div>Waveform: {editorStore.getWaveformData() ? 'Available' : 'Not available'}</div>
              <div>Container: {editorStore.container ? 'Available' : 'Not available'}</div>
            </DebugSection>
          </LoadingContainer>
        ) : (
          <>
            <TranscriptView
              container={editorStore.container}
              editorStore={editorStore}
              onFineAlign={handleFineAlignment}
              onForceAlign={handleProcessCurrentSelection}
              searchTerm={searchTerm}
              visibleParagraphIds={visibleParagraphIds}
            />
          </>
        )}
      </ChunksContainer>
      
      {/* Right column - Speakers Panel */}
      <SpeakersPanel editorStore={editorStore} />
      
      {/* Bottom row - Waveform (left) and Video (right) */}
      <WaveformContainer>
        <TranscriptWaveform 
          waveformData={editorStore.waveformData ? editorStore.waveformData.toJSON().data : []}
          duration={editorStore.container && editorStore.container.segments.length > 0 
            ? Math.max(...editorStore.container.segments.map(s => s.endTime))
            : 0
          }
          speakerSegments={editorStore.container && editorStore.container.segments.length > 0 
            ? editorStore.container.segments.map(segment => ({
                startTime: segment.startTime,
                endTime: segment.endTime,
                speakerColor: segment.speakerLabel 
                    ? editorStore.editor.getSpeakerService()?.getSpeakerColor(segment.speakerLabel) || '#A9A9A9'
                    : '#A9A9A9'
            }))
            : []
          }
        />
      </WaveformContainer>
      
      {/* Video preview panel */}
      <VideoPlayerPreview/>

      {/* Fine Alignment Modal */}
      {showFineAlignmentModal && (
        <FineAlignmentModal
          isOpen={showFineAlignmentModal}
          position={fineAlignmentPosition}
          selectedElements={selectedElementsForAlignment}
          onClose={() => setShowFineAlignmentModal(false)}
          editorStore={editorStore}
          audioUrl={editorStore.editor.getAudioUrl()}
          waveformData={editorStore.waveformData}
          onForceAlign={handleProcessCurrentSelection}
        />
      )}

      {/* Alignment Results Modal */}
      {showAlignmentModal && alignmentResult && (
        <AlignmentResultsModal>
          <AlignmentModalHeader>
            <AlignmentModalTitle>Forced Alignment Results</AlignmentModalTitle>
            <CloseButton onClick={() => setShowAlignmentModal(false)}>
              <CloseIcon width="20" height="20" />
            </CloseButton>
          </AlignmentModalHeader>
          
          <AlignmentComparisonContainer>
            <AlignmentSection>
              <AlignmentSectionTitle>Before Alignment</AlignmentSectionTitle>
              <AlignmentWordsTable>
                <thead>
                  <tr>
                    <th>Word</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                  </tr>
                </thead>
                <tbody>
                  {alignmentResult.before.map((item, index) => (
                    <tr key={`before-${index}`}>
                      <td>{item.word}</td>
                      <td>{item.startTime !== undefined ? item.startTime.toFixed(3) : 'N/A'}</td>
                      <td>{item.endTime !== undefined ? item.endTime.toFixed(3) : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </AlignmentWordsTable>
            </AlignmentSection>
            
            <AlignmentSection>
              <AlignmentSectionTitle>After Alignment</AlignmentSectionTitle>
              <AlignmentWordsTable>
                <thead>
                  <tr>
                    <th>Word</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {alignmentResult.after.map((item, index) => (
                    <tr key={`after-${index}`}>
                      <td>{item.word}</td>
                      <td>{item.startTime !== undefined ? item.startTime.toFixed(3) : 'N/A'}</td>
                      <td>{item.endTime !== undefined ? item.endTime.toFixed(3) : 'N/A'}</td>
                      <td>{item.confidence !== undefined ? (item.confidence * 100).toFixed(1) + '%' : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </AlignmentWordsTable>
            </AlignmentSection>
          </AlignmentComparisonContainer>
          
          <AlignmentModalFooter>
            <Button onClick={() => setShowAlignmentModal(false)}>Close</Button>
          </AlignmentModalFooter>
        </AlignmentResultsModal>
      )}

      {/* Version switching modal */}
      {showVersionSwitchModal && targetVersion && (
        <TranscriptVersionSwitchModal
          isOpen={showVersionSwitchModal}
          targetVersion={targetVersion}
          onConfirm={() => {
            // Switch to the selected version
            editorStore.loadTranscriptVersion(targetVersion);
            
            // Track analytics
            analytics.track('transcript_version_switch', {
              previousVersion: editorStore.currentTranscriptVersion,
              newVersion: targetVersion,
              storyId: editorStore.storyId,
            });
            
            // Close the modal
            setShowVersionSwitchModal(false);
          }}
          onCancel={() => {
            setShowVersionSwitchModal(false);
            setTargetVersion(null);
          }}
        />
      )}
      </Container>
    </VideoPlayerProvider>
  );
});

// Debugging styled components
const DebugSection = styled.div`
  margin-top: 20px;
  font-size: 14px;
  color: #ffaa00;
  text-align: left;
  background-color: rgba(0, 0, 0, 0.3);
  padding: 10px;
  border-radius: 4px;
  font-style: normal;
`;

const DebugInfo = styled.div`
  font-size: 14px;
  color: #777;
  margin-bottom: 10px;
  font-style: italic;
`;

// Styled components
const Container = styled.div<{ standalone?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 420px;
  grid-template-rows: 75px 1fr 150px 150px;
  grid-template-areas:
    "header speakers"
    "transcript speakers"
    "transcript video"
    "waveform video";
  height: ${props => props.standalone ? '100vh' : '100%'};
  width: 100%;
  color: #FFFFFF;
  overflow: hidden;
  border-radius: 0;
`;

const WaveformContainer = styled.div`
  grid-area: waveform;
  height: 150px;
  width: 100%;
  box-sizing: border-box;
  border-right: 1px solid #1a1e23;
`;

const ChunksContainer = styled.div`
  grid-area: transcript;
  overflow-y: auto;
  padding: 0;
  height: 100%;
  border-right: 1px solid #1a1e23;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 20px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  gap: 15px;
`;

const ErrorTitle = styled.h2`
  font-size: 20px;
  color: #ff6961;
  margin: 0;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #f3e9d7;
  text-align: center;
  max-width: 500px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: #A9A9A9;
  text-align: center;
  font-style: italic;
  background-color: #1e1e2c;
  border-radius: 4px;
  margin-top: 20px;
`;

// Alignment modal components
const AlignmentResultsModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  max-width: 1200px;
  background-color: #030419;
  border: 1px solid #1e1e2c;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
`;

const AlignmentModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #1e1e2c;
`;

const AlignmentModalTitle = styled.h2`
  margin: 0;
  color: #f2d093;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #f3e9d7;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #ffffff;
  }
`;

const AlignmentComparisonContainer = styled.div`
  display: flex;
  gap: 20px;
  padding: 20px;
  height: calc(100% - 130px);
  overflow: auto;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const AlignmentSection = styled.div`
  flex: 1;
  background-color: #0d0e24;
  border-radius: 6px;
  padding: 15px;
  height: 100%;
  overflow: auto;
`;

const AlignmentSectionTitle = styled.h3`
  color: #f3e9d7;
  font-size: 16px;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #1e1e2c;
`;

const AlignmentWordsTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #1e1e2c;
  }

  th {
    color: #f2d093;
    font-weight: 600;
    font-size: 14px;
  }

  td {
    color: #f3e9d7;
    font-size: 14px;
  }

  tr:hover td {
    background-color: rgba(242, 208, 147, 0.05);
  }
`;

const AlignmentModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  border-top: 1px solid #1e1e2c;
`;

const Button = styled.button<{ primary?: boolean; danger?: boolean }>`
  background-color: ${props => {
    if (props.primary) return '#f2d093';
    if (props.danger) return 'rgba(255, 105, 97, 0.2)';
    return 'transparent';
  }};
  color: ${props => {
    if (props.primary) return '#030419';
    if (props.danger) return '#ff6961';
    return '#f3e9d7';
  }};
  border: 1px solid ${props => {
    if (props.primary) return '#f2d093';
    if (props.danger) return '#ff6961';
    return '#484848';
  }};
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => {
      if (props.primary) return '#d9b77e';
      if (props.danger) return 'rgba(255, 105, 97, 0.3)';
      return 'rgba(242, 208, 147, 0.1)';
    }};
  }
`;


export default TranscriptEditorWrapper;